import { useCallback, useRef, useState } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { MessageCircle, Wrench, X } from "lucide-react";
import { formatIDR, pasangCopy, pasangZones } from "@kanata/shared";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { dismissPasangNotice, pasangNoticeDismissed, pasangWhatsappUrl } from "@/lib/pasang-notice";

interface Pending {
  productNames: string[];
  /** Run when the customer confirms. Absent when the notice is only telling them. */
  onContinue?: () => void;
  /**
   * Show it even if the customer ticked "Jangan tampilkan ini lagi". For the
   * step before paying: the last point at which they can still ask first.
   */
  always?: boolean;
}

/**
 * The notice that pasang is booked by hand, not guaranteed by the checkbox.
 *
 * `show` opens it — unless the customer already ticked "Jangan tampilkan ini
 * lagi" and the caller did not ask for `always`, in which case `onContinue`
 * runs straight away. Render `dialog` somewhere in the page.
 */
export function usePasangNotice() {
  const [pending, setPending] = useState<Pending | null>(null);
  const [dontShow, setDontShow] = useState(false);
  // Read at close time, so a tick followed by Esc still counts.
  const dontShowRef = useRef(false);
  dontShowRef.current = dontShow;

  const show = useCallback((p: Pending = { productNames: [] }) => {
    if (!p.always && pasangNoticeDismissed()) {
      p.onContinue?.();
      return;
    }
    setDontShow(false);
    setPending(p);
  }, []);

  function close(confirmed: boolean) {
    if (dontShowRef.current) dismissPasangNotice();
    const next = confirmed ? pending?.onContinue : undefined;
    setPending(null);
    next?.();
  }

  const dialog = (
    <DialogPrimitive.Root open={pending !== null} onOpenChange={(open) => !open && close(false)}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-ink/40 backdrop-blur-[2px] data-[state=closed]:animate-out data-[state=closed]:fade-out data-[state=open]:animate-in data-[state=open]:fade-in" />
        <DialogPrimitive.Content className="fixed left-1/2 top-1/2 z-50 max-h-[calc(100dvh-2rem)] w-[calc(100%-2rem)] max-w-lg overflow-y-auto -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-white p-6 shadow-2xl data-[state=closed]:animate-out data-[state=closed]:fade-out data-[state=open]:animate-in data-[state=open]:fade-in">
          <span className="flex size-10 items-center justify-center rounded-full bg-brand-light text-brand">
            <Wrench className="size-5" />
          </span>
          <DialogPrimitive.Title className="mt-4 text-lg font-medium text-ink">
            {pasangCopy.notice.title}
          </DialogPrimitive.Title>
          <DialogPrimitive.Description className="mt-2 text-sm leading-relaxed text-ink/65">
            {pasangCopy.notice.body}
          </DialogPrimitive.Description>

          <div className="mt-5 overflow-hidden rounded-xl border border-ink/10">
            <table className="w-full text-left text-sm">
              <caption className="bg-brand-tint px-4 py-2.5 text-left font-medium text-ink">
                {pasangCopy.notice.tableCaption}
              </caption>
              <thead className="border-y border-ink/10 text-xs uppercase tracking-wider text-ink/50">
                <tr>
                  <th scope="col" className="px-4 py-2 font-medium">
                    {pasangCopy.notice.tableHeaders.zone}
                  </th>
                  <th scope="col" className="px-4 py-2 font-medium">
                    {pasangCopy.notice.tableHeaders.areas}
                  </th>
                  <th scope="col" className="px-4 py-2 text-right font-medium">
                    {pasangCopy.notice.tableHeaders.estimate}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink/10">
                {pasangZones.map((z) => (
                  <tr key={z.zone} className="align-top">
                    <th scope="row" className="whitespace-nowrap px-4 py-2.5 font-medium text-ink">
                      {z.zone}
                    </th>
                    <td className="px-4 py-2.5 text-ink/70">{z.areas.join(", ")}</td>
                    <td className="whitespace-nowrap px-4 py-2.5 text-right font-medium text-ink">
                      {formatIDR(z.estimateIdr)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-3 text-xs leading-relaxed text-ink/55">{pasangCopy.notice.footnote}</p>

          {!pending?.always && (
            <label className="mt-5 flex cursor-pointer items-center gap-2 text-sm text-ink/70">
              <Checkbox checked={dontShow} onCheckedChange={(v) => setDontShow(v === true)} />
              {pasangCopy.notice.dismiss}
            </label>
          )}

          <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button asChild variant="outline">
              <a href={pasangWhatsappUrl(pending?.productNames)} target="_blank" rel="noopener noreferrer" data-wa-placement="pasang_notice">
                <MessageCircle />
                {pasangCopy.notice.whatsapp}
              </a>
            </Button>
            <Button type="button" onClick={() => close(true)}>
              {pending?.onContinue ? pasangCopy.notice.continue : pasangCopy.notice.ok}
            </Button>
          </div>

          <DialogPrimitive.Close className="absolute right-4 top-4 rounded-full p-2 text-ink/50 transition-colors hover:bg-ink/[0.05] hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/25">
            <X className="size-4" />
            <span className="sr-only">Tutup</span>
          </DialogPrimitive.Close>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );

  return { show, dialog };
}
